<script setup lang="ts">
import type { INewsDataClientDTO } from '~/shared/api/client/news/news.dto';
import { resizeCropSlot } from '~/shared/lib/resizeImage';
import { CROP_RATIO_CHRONICLE } from '~/shared/lib/crop-ratio.const';
import { NEWS_ITEM_BIG_IMAGE_WIDTH } from './lib/const';

const props = defineProps<INewsDataClientDTO>();

const isRedirect = computed(() =>
  Boolean(props.news.redirect && props.news.redirect.length > 0),
);

const link =
  isRedirect.value && props.news.redirect
    ? props.news.redirect
    : `/${props.news.type}/${props.news.id}`;

const imageOne =
  props.image_one?.file_url || props.news.image?.photo.file_url || undefined;
const categoryLink = `/category/${props.news.category?.id}`;

const imageOneAttrs = computed(() =>
  resizeCropSlot(imageOne, CROP_RATIO_CHRONICLE.background_giant, 0, {
    width: NEWS_ITEM_BIG_IMAGE_WIDTH,
  }),
);
</script>

<template>
  <div :class="$style.item">
    <NuxtLink
      :to="link"
      :class="$style.item__link"
      :target="isRedirect ? '_blank' : undefined"
    >
      <img
        v-if="imageOne"
        :src="imageOneAttrs.src"
        :srcset="imageOneAttrs.srcset || undefined"
        :alt="props.title"
      />
      <div :class="$style.item__info">
        <div
          :class="$style.item__category"
          :to="categoryLink"
          @click.stop="navigateTo(categoryLink)"
        >
          {{ props.news.category?.name }}
        </div>
        <div :class="$style.item__title">
          {{ props.title }}
        </div>
      </div>
    </NuxtLink>
  </div>
</template>

<style lang="scss" module>
.item {
  height: 100%;
  &__link {
    display: flex;
    flex-flow: column nowrap;
    position: relative;

    &::before {
      position: absolute;
      content: '';
      left: 0px;
      right: 0px;
      top: 0px;
      bottom: 0px;

      background: #111111;

      opacity: 0.4;
    }
  }

  &__info {
    display: flex;
    flex-flow: column nowrap;
    margin-top: auto;
    text-align: left;
    gap: var(--space-1x);
    padding: var(--space-2-5x) var(--space-2x);
    position: absolute;
    bottom: 0;
    left: 0;
  }

  &__category {
    @include text_caption2_caps;
    color: var(--color-white);
    z-index: 1;
  }

  &__title {
    @include text_h5;
    color: var(--color-white);
    z-index: 1;
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 479px) {
  .item {
    margin: -20px;
    &__title {
      @include text_body2;
    }
  }
}
</style>
